// Who owns a composed forecast and which organization can read it. Org members read a forecast, its data sources
// and its asset links only through the row's organizationId (groupsField rule), so it must be the target org's,
// never left blank because an Admin built it.

export interface OrganizationSummary {
  owner?: string | null;
}

export interface ForecastOwnership {
  /** Undefined = let AppSync stamp the caller (its own sub::username format, safe for every login type) */
  owner?: string;
  organizationId: string | null;
}

interface ResolveForecastOwnershipArgs {
  /** Org picked in the form ("" = no organization) */
  selectedOrganizationId: string;
  /** user.organizations.data — every org for an Admin, the user's own orgs otherwise */
  organizations: Record<string, OrganizationSummary>;
  isAdmin: boolean;
  /** The saved forecast when editing */
  existing?: { owner?: string | null; organizationId?: string | null };
}

/**
 * The owner and org to save on a forecast (and stamp on its data sources).
 *  - An Admin assigning a different org hands ownership to that org's owner, as asset groups do, so the org can
 *    manage it.
 *  - Otherwise the saved owner is kept (edit), or the caller becomes the owner (create).
 */
export function resolveForecastOwnership({
  selectedOrganizationId,
  organizations,
  isAdmin,
  existing,
}: ResolveForecastOwnershipArgs): ForecastOwnership {
  const organizationId = selectedOrganizationId || null;
  const orgChanged = !existing || organizationId !== (existing.organizationId || null);
  const orgOwner = organizationId ? organizations[organizationId]?.owner : null;

  if (isAdmin && orgChanged && orgOwner) return { owner: orgOwner, organizationId };
  return { owner: existing?.owner || undefined, organizationId };
}

/**
 * Ids of saved data sources that stay on the forecast, aren't already being rewritten (`rewrittenIds`), and don't
 * carry the forecast's owner and org — they get only their ownership updated.
 */
export function sourcesNeedingRestamp(
  savedSources: { id: string; owner?: string | null; organizationId?: string | null }[],
  keptIds: Set<string>,
  rewrittenIds: Set<string>,
  target: { owner?: string | null; organizationId: string | null }
): string[] {
  return savedSources
    .filter((ds) => keptIds.has(ds.id) && !rewrittenIds.has(ds.id))
    .filter(
      (ds) =>
        (ds.organizationId || null) !== target.organizationId || (!!target.owner && ds.owner !== target.owner)
    )
    .map((ds) => ds.id);
}

/**
 * Mirrors the ComposedForecast @auth: Admins and the stored owner can edit, delete and assign; org members only
 * read. `ownerCandidates` covers every format an owner is stored in (sub, username, sub::username).
 */
export function canManageComposedForecast(
  forecast: { owner?: string | null } | null | undefined,
  groups: string[] | null | undefined,
  ownerCandidates: string[]
): boolean {
  if (groups?.includes("Admin")) return true;
  return !!forecast?.owner && ownerCandidates.includes(forecast.owner);
}

/** The org a new forecast starts on: the creator's first organization, if any. */
export function defaultForecastOrganizationId(userOrganizationIds?: (string | null)[] | null): string {
  return userOrganizationIds?.find((id): id is string => !!id) ?? "";
}
